import type { DateRange } from '@branchjs/neo-components';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import startOfQuarter from 'date-fns/startOfQuarter';
import startOfWeek from 'date-fns/startOfWeek';
import subDays from 'date-fns/subDays';
import type { DateRange as StringBasedDateRange } from '../../types/dateRangeTypes';
import { formatToDefaultDateFormat } from '../../support/Dates/dateUtils';

export type DateRangeVariant = (typeof DATE_RANGES_KEYS)[number];
export const DATE_RANGES_KEYS = [
  'today',
  'yesterday',
  'last_7_days',
  'last_14_days',
  'last_30_days',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_quarter',
  'last_quarter',
  'custom',
] as const;

function isNumericRangeVariant(rangeInDays: any): rangeInDays is number {
  return typeof rangeInDays === 'number';
}

export const mapRangeInDaysToRangeVariant = (defaultRange?: number): DateRangeVariant => {
  switch (defaultRange) {
    case 1:
      return 'yesterday';
    case 3:
      return 'last_7_days';
    case 7:
      return 'last_7_days';
    case 14:
      return 'last_14_days';
    case 30:
      return 'last_30_days';
    case 90:
      return 'last_quarter';
    default:
      return 'custom';
  }
};

export const mapRangeVariantToDateRange = (rangeVariant: DateRangeVariant): DateRange<Date> => {
  switch (rangeVariant) {
    case 'yesterday': {
      const yesterday = subDays(startOfDay(new Date()), 1);
      return [yesterday, yesterday];
    }
    case 'last_7_days': {
      const today = startOfDay(new Date());
      const sevenDaysAgo = subDays(today, 7);
      return [sevenDaysAgo, today];
    }
    case 'last_14_days': {
      const today = startOfDay(new Date());
      const fourteenDaysAgo = subDays(today, 14);
      return [fourteenDaysAgo, today];
    }
    case 'last_30_days': {
      const today = startOfDay(new Date());
      const thirtyDaysAgo = subDays(today, 30);
      return [thirtyDaysAgo, today];
    }
    case 'this_week': {
      const today = startOfDay(new Date());
      const firstDayOfWeek = startOfWeek(today);
      return [firstDayOfWeek, today];
    }
    case 'last_week': {
      const today = startOfDay(new Date());
      const firstDayOfWeek = startOfWeek(today);
      const lastDayOfPreviousWeek = subDays(firstDayOfWeek, 1);
      const firstDayOfPreviousWeek = startOfWeek(lastDayOfPreviousWeek);
      return [firstDayOfPreviousWeek, lastDayOfPreviousWeek];
    }
    case 'this_month': {
      const today = startOfDay(new Date());
      const firstDayOfMonth = startOfMonth(today);
      return [firstDayOfMonth, today];
    }
    case 'last_month': {
      const today = startOfDay(new Date());
      const firstDayOfMonth = startOfMonth(today);
      const lastDayOfPreviousMonth = subDays(firstDayOfMonth, 1);
      const firstDayOfPreviousMonth = startOfMonth(lastDayOfPreviousMonth);
      return [firstDayOfPreviousMonth, lastDayOfPreviousMonth];
    }
    case 'this_quarter': {
      const today = startOfDay(new Date());
      const firstDayOfQuarter = startOfQuarter(today);
      return [firstDayOfQuarter, today];
    }
    case 'last_quarter': {
      const today = startOfDay(new Date());
      const firstDayOfQuarter = startOfQuarter(today);
      const lastDayOfPreviousQuarter = subDays(firstDayOfQuarter, 1);
      const firstDayOfPreviousQuarter = startOfQuarter(lastDayOfPreviousQuarter);
      return [firstDayOfPreviousQuarter, lastDayOfPreviousQuarter];
    }
    case 'today':
    case 'custom':
    default: {
      const today = startOfDay(new Date());
      return [today, today];
    }
  }
};

export const convertDateRangeVariantToStringRange = (rangeVariant: DateRangeVariant): StringBasedDateRange => {
  const dateRange = mapRangeVariantToDateRange(rangeVariant);

  return {
    start_date: dateRange[0] ? formatToDefaultDateFormat(dateRange[0]) : '',
    end_date: dateRange[1] ? formatToDefaultDateFormat(dateRange[1]) : '',
  };
};

export const convertToValidDateRangeVariant = (rangeVariant: DateRangeVariant | number): DateRangeVariant => {
  if (isNumericRangeVariant(rangeVariant)) {
    const stringRangeVariant = mapRangeInDaysToRangeVariant(rangeVariant);

    return stringRangeVariant;
  }

  return rangeVariant;
};
